import { useTranslation } from "react-i18next"
import { alpha, Box, Divider, Skeleton, Stack, Typography } from "@mui/material"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import fp from "lodash/fp"

import type { Theme } from "@mui/material"
import type { KeyboardEvent } from "react"
import type { Conductor } from "types/conductors.types"

import Chip from "widgets/common/Chip"
import {
  conductorName,
  formatConductorLength,
  formatConductorSection,
} from "helpers/utils/conductors"

interface ConductorMobileListProps {
  conductors: Conductor[]
  /** Skeleton blocks to stand in while the conductors load. */
  loadingCount?: number
  onSelect: (conductor: Conductor) => void
}

/** The tint the untriggered alert accordions carry further down the page. */
const outline = (theme: Theme) => alpha(theme.palette.action.disabled, 0.5)

interface MeasurementProps {
  labelKey: string
  value: string
}

const Measurement = ({ labelKey, value }: MeasurementProps) => {
  const { t } = useTranslation()

  return (
    <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="overlineDense">{t(labelKey)}</Typography>
      <Typography noWrap fontWeight={600}>
        {value}
      </Typography>
    </Stack>
  )
}

/**
 * The conductors of a power line, one block each. A table this narrow can only
 * squash its columns or scroll them out of reach, so a conductor is headed by
 * what identifies it and the two measurements sit below.
 */
const ConductorMobileList = ({
  conductors,
  loadingCount = 0,
  onSelect,
}: ConductorMobileListProps) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"

  if (loadingCount > 0) {
    return (
      <Stack spacing={1.5}>
        {Array.from({ length: loadingCount }, (_, index) => (
          <Skeleton key={index} variant="rounded" height={144} />
        ))}
      </Stack>
    )
  }

  return (
    <Stack spacing={1.5}>
      {conductors.map((conductor) => (
        <Box
          key={conductor.id}
          role="button"
          // A box is not focusable on its own, so let the keyboard in.
          tabIndex={0}
          onClick={() => onSelect(conductor)}
          onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault()
              onSelect(conductor)
            }
          }}
          sx={{
            p: 2,
            cursor: "pointer",
            borderRadius: 1,
            border: (theme) => `1px solid ${outline(theme)}`,
            backgroundColor: (theme) => alpha(theme.palette.action.disabled, 0.05),
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {/* The code is what identifies the conductor, so the name gives way
                to it rather than the other way round. */}
            <Chip label={conductor.type} sx={{ flexShrink: 0 }} />
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                noWrap
                fontWeight={600}
                fontStyle={conductor.name ? "normal" : "italic"}
              >
                {conductorName(t, conductor)}
              </Typography>
              <Typography noWrap variant="body2" color="neutral.600">
                {conductor.manufacturer ?? "-"}
              </Typography>
            </Stack>
            <KeyboardArrowRightIcon
              fontSize="small"
              sx={{ display: "block", color: "text.disabled" }}
            />
          </Stack>
          <Divider sx={{ my: 1.5, borderColor: outline }} />
          <Stack direction="row" spacing={2}>
            <Measurement
              labelKey="conductors.NOMINAL_SECTION"
              value={
                fp.isNil(conductor.nominal_section)
                  ? "-"
                  : formatConductorSection(conductor.nominal_section, locale)
              }
            />
            <Measurement
              labelKey="conductors.DIAMETER"
              value={formatConductorLength(conductor.diameter, locale)}
            />
          </Stack>
        </Box>
      ))}
    </Stack>
  )
}

export default ConductorMobileList
